import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { environment } from '../../../../../../environments/environment'

import { flashMessages } from '../../../../../../locale/flash-messages/flash-messages.en'
import { metaDataContent } from '../../../../../../locale/meta-data/meta-data.content.en'
import { appConstants } from '../../../../../app.constants'
import { ResourceSlug } from '../../../../../shared/enums/wordpress/wp-resource-slug.enum'
import { BreadcrumbLink } from '../../../../../shared/interfaces/breadcrumb-link.interface'
import { SelectOption } from '../../../../../shared/interfaces/select-option.interface'
import { BreadcrumbService } from '../../../../../shared/services/breadcrumb.service'
import { CatalogueService } from '../../../../../shared/services/catalogue.service'
import { FlashMessageService } from '../../../../../shared/services/flash-message.service'
import { MetaService } from '../../../../../shared/services/meta.service'
import { Tool } from '../../../interfaces/tool.interface'
import { WordCountValidators } from '../../../partials/rich-text-input/word-count.validator'
import { ToolUseCaseService } from '../../../services/tool-use-case.service'
import { ToolService } from '../../../services/tool.service'
import { ToolUseCaseDto } from '../dtos/tool-use-case.dto'

@Component({
  selector: 'app-tool-use-case-create',
  templateUrl: './tool-use-case-create.component.html',
  styleUrls: ['./tool-use-case-create.component.scss']
})
export class ToolUseCaseCreateComponent implements OnInit {
  tool: Tool
  breadcrumbLinks: BreadcrumbLink[]

  createTaxonomyFunction: (
    slug: string,
    name: string
  ) => Promise<SelectOption[]> = (slug: string, name: string) =>
    this.catalogueService.store(slug, { name })

  taxonomyGroups: {
    name: string
    description?: string
    taxonomies: {
      label: string
      placeholder: string
      slug: ResourceSlug
      propName: string
      maxSelectedItems?: number
      options?: SelectOption[]
      createFunction?: (name: string) => Promise<SelectOption[]>
    }[]
  }[] = [
    {
      name: 'More information about the use case',
      taxonomies: [
        {
          label: 'Objectives covered by the use case (max. 3)',
          propName: 'objectiveIds',
          placeholder: `Select the objective(s) covered by the use case...`,
          slug: ResourceSlug.Objectives,
          maxSelectedItems: 3
        },
        {
          label: ' Country of the use case',
          propName: 'countryIds',
          placeholder: 'Select the countries of the use case...',
          slug: ResourceSlug.Countries
        },
        {
          label: 'AI lifecycle stages illustrated by the use case (max. 3)',
          propName: 'lifecycleIds',
          placeholder: 'Select the conditions for using the tool...',
          slug: ResourceSlug.Lifecycles,
          maxSelectedItems: 3
        },
        {
          label: 'Impacted stakeholders by the use case (max. 3)',
          propName: 'impactedStakeholderIds',
          placeholder:
            'Select the type of stakeholder group(s) impacted by the use case...',
          slug: ResourceSlug.ImpactedStakeholder,
          maxSelectedItems: 3
        },
        {
          label: ' Target sectors (max. 3)',
          propName: 'targetSectorIds',
          placeholder: 'Select the target sectors of the use case...',
          slug: ResourceSlug.TargetSectors,
          maxSelectedItems: 3
        },
        {
          label: ' Target users (max. 3)',
          propName: 'targetUserIds',
          placeholder: 'Select the target users of the use case...',
          slug: ResourceSlug.TargetUsers,
          maxSelectedItems: 3
        },
        {
          label: 'Purposes or action of the AI system (max. 3)',
          propName: 'purposeIds',
          placeholder: 'Select the purpose or action of the AI system...',
          slug: ResourceSlug.Purposes,
          maxSelectedItems: 3,
          createFunction: (newTermName: string) =>
            this.createTaxonomyFunction(
              `taxonomies/${ResourceSlug.Purposes}`,
              newTermName
            )
        }
      ]
    }
  ]

  form: FormGroup

  captchaKey = appConstants.RECAPTCHA_SITE_KEY
  showErrors: boolean
  invalidInputs: string[]
  isRelatedPostInvalid: boolean
  loadingSubmit: boolean
  showImageGalleryModal: boolean
  storagePath: string = environment.apiCatalogStoragePath

  searchToolFunction: (
    terms: string
  ) => Promise<{ id: number; label: string }[]>

  constructor(
    private formBuilder: FormBuilder,
    private toolUseCaseService: ToolUseCaseService,
    private catalogueService: CatalogueService,
    private toolService: ToolService,
    private flashMessageService: FlashMessageService,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    metaService: MetaService
  ) {
    this.breadcrumbLinks = breadcrumbService.getBreadCrumbLinks('toolCatalog', [
      {
        label: 'Tool use case',
        path: '/catalogue/tool-use-cases'
      },
      {
        label: 'Submit a use case for a tool'
      }
    ])
    metaService.setTags(metaDataContent.toolCatalogUseCaseSubmit)
    this.searchToolFunction = (terms: string) =>
      this.toolService.listUnpaginated({ terms }).then((res: Tool[]) =>
        res.map((tool: Tool) => ({
          id: tool.id,
          label: tool.name
        }))
      )
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required, WordCountValidators.max(1000)]],
      benefits: '',
      shortcomings: '',
      shortSentence: ['', [Validators.required, WordCountValidators.max(30)]],
      learnings: '',
      comparison: '',
      image: '',
      defaultImage: '',
      email: ['', [Validators.email, Validators.required]],
      submitter: ['', [Validators.required]],
      recaptchaReactive: [null, Validators.required],
      consent: [false, Validators.requiredTrue],
      consentPublicationDelay: [false, Validators.requiredTrue],
      toolId: [null, Validators.required]
    })

    this.activatedRoute.queryParams.subscribe((params: Params) => {
      if (params?.toolSlug) {
        this.toolService
          .show(params.toolSlug)
          .then((toolRes: Tool) => {
            this.tool = toolRes

            this.breadcrumbLinks = this.breadcrumbService.getBreadCrumbLinks(
              'toolCatalog',
              [
                {
                  label: this.tool.name,
                  path: `/catalogue/tools/${this.tool.slug}`
                },
                { label: 'Submit a use case for this tool' }
              ]
            )
            this.form.get('toolId').setValue(this.tool.id)
          })
          .catch((err) => {
            this.flashMessageService.error(err.message)
          })
      }
    })
    this.taxonomyGroups.forEach((taxonomyGroup) => {
      taxonomyGroup.taxonomies.forEach((taxonomy) => {
        // Add dynamic form control.
        this.form.addControl(taxonomy.propName, new FormControl([]))

        this.catalogueService
          .listSelectOptions(taxonomy.slug)
          .then((filterTaxonomyRes: SelectOption[]) => {
            taxonomy.options = filterTaxonomyRes
          })
      })
    })
  }

  onFieldChanged(event: any, formControlName: string) {
    this.form.get(formControlName).setValue(event)
  }

  onToolSelected(toolIds: number[]) {
    this.form.get('toolId').setValue(toolIds[0])
  }

  submit(formValue) {
    if (this.form.invalid) {
      this.invalidInputs = this.debugFindInvalidControls()
      this.showErrors = true
      return this.flashMessageService.error(
        'Please fill in all required fields and click on the CAPTCHA to submit the tool.'
      )
    }
    if (formValue.shortSentence.length > 250) {
      return this.flashMessageService.error('The maximum of words is 30')
    }

    this.loadingSubmit = true

    const useCaseDto: ToolUseCaseDto = {
      name: formValue.title,
      toolId: formValue.toolId,
      benefits: formValue.benefits,
      shortcomings: formValue.shortcomings,
      learnings: formValue.learnings,
      comparison: formValue.comparison,
      email: formValue.email,
      submitter: formValue.submitter,
      shortSentence: formValue.shortSentence,
      content: formValue.description,
      image: formValue.image || formValue.defaultImage
    }

    // Apply dynamic taxonomy input values to dto.
    this.taxonomyGroups.forEach((taxonomyGroup) => {
      taxonomyGroup.taxonomies.forEach((taxonomy) => {
        if (formValue[taxonomy.propName]) {
          useCaseDto[taxonomy.propName] = formValue[taxonomy.propName]
        }
      })
    })

    this.toolUseCaseService.store(useCaseDto).then(
      async () => {
        this.router.navigate(['/catalogue/tool-use-cases/submit/success'])
        this.loadingSubmit = false
      },
      () => {
        this.flashMessageService.error(flashMessages.tool.error.cannotSend)
        this.loadingSubmit = false
      }
    )
  }

  private debugFindInvalidControls(): string[] {
    const invalid = []
    const controls = this.form.controls
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name)
      }
    }
    console.log(invalid)
    return invalid
  }
}
